import { css } from '@styled-system/css';
import { type FC } from 'react';
import Skeleton from 'react-loading-skeleton';

import { useIsMobile } from '@/shared/hooks/useIsMobile';
import { formatPrice } from '@/shared/utils/format';

export interface PriceDistributionCardProps {
  q1Price: number | null;
  q3Price: number | null;
  loading?: boolean;
}

export const PriceDistributionCard: FC<PriceDistributionCardProps> = ({ q1Price, q3Price, loading }) => {
  const isMobile = useIsMobile();

  const cardStyles = css({
    bg: 'base.segBg', // #16171b
    border: '0.75px solid',
    borderColor: 'base.border', // #1e1f24
    borderRadius: '0.625rem', // 10px
    height: '5.625rem', // 90px
    w: '100%',
    p: '1.0625rem 0.875rem', // 17px 14px
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  });

  const rowStyles = css({
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    fontSize: 'sm', // 11px
    fontFamily: 'Manrope, sans-serif',
    lineHeight: '1.07',
  });

  const labelStyles = css({
    color: 'font.segondary', // #b3b5bd
    fontWeight: 'medium',
  });

  const textStyles = css({
    color: 'font.lowkey', // #4f5055
    fontWeight: 'medium',
  });

  const priceStyles = css({
    color: 'accent.primary', // #5e69d3
    fontWeight: 'bold',
  });

  return (
    <div className={cardStyles}>
      <div className={rowStyles}>
        <span className={labelStyles}>1 produit sur 4</span>
        <span className={textStyles}>est vendu à moins de</span>
        <span className={priceStyles}>
          {loading ? <Skeleton width="4.375rem" height="sm" /> : formatPrice(q1Price)}
        </span>
        {!isMobile && (
          <span className={textStyles}>
            il faut viser <span className={css({ color: 'accent.primary' })}>l'achat</span> à ce prix.
          </span>
        )}
      </div>
      <div className={rowStyles}>
        <span className={labelStyles}>2 produits sur 4</span>
        <span className={textStyles}>est vendu entre</span>
        <span className={priceStyles}>
          {loading ? (
            <Skeleton width="7.5rem" height="sm" />
          ) : (
            <>
              {formatPrice(q1Price)} <span className={textStyles}>et</span> {formatPrice(q3Price)}
            </>
          )}
        </span>
      </div>
      <div className={rowStyles}>
        <span className={labelStyles}>1 produit sur 4</span>
        <span className={textStyles}>est vendu à plus de</span>
        <span className={priceStyles}>
          {loading ? <Skeleton width="4.375rem" height="sm" /> : formatPrice(q3Price)}
        </span>
        {!isMobile && (
          <span className={textStyles}>
            il faut viser <span className={css({ color: 'accent.primary' })}>la revente</span> à ce prix.
          </span>
        )}
      </div>
    </div>
  );
};
